import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Minus, Rows2 } from 'lucide-react';
import { TechnicianColumnTitle } from './TechnicianColumnTitle';
import { TechnicianColumnOsV2Badges } from './TechnicianColumnOsV2Badges';
import { TechnicianMenuTrigger } from './TechnicianMenuTrigger';
import TechnicianColumnSessionDropOverlay from './TechnicianColumnSessionDropOverlay';
import { canShowNovoPainelMenuItem } from './TechnicianMenuModals';
import { isBoardLiveServiceGroup } from '../constants/boardGroups';
import { getAllTechnicianOrdersForCondtTabs } from '../utils/futuraRetornoCondt';
import { getBoardDragOrderIds, isBoardOrderDragEvent } from '../utils/boardOrderDrag';

export function lookupByTechId(mapOrSet, technicianId, method) {
  if (!mapOrSet || technicianId == null || technicianId === '') return method === 'get' ? null : false;
  const raw = String(technicianId);
  const digits = raw.replace(/\D/g, '');
  const padded = digits ? digits.padStart(4, '0').slice(-4) : '';
  const unpadded = digits.replace(/^0+/, '') || (digits ? '0' : '');
  const keys = [raw, digits, padded, unpadded].filter(Boolean);
  if (method === 'has') {
    return keys.some((k) => mapOrSet.has(k));
  }
  for (const k of keys) {
    const value = mapOrSet.get(k);
    if (value) return value;
  }
  return null;
}

/**
 * Coluna de técnico do Board. Fora de App para não remontar a cada render
 * (preserva scroll, drag e overlay).
 */
function TechnicianColumn({
  technician,
  orders: _orders,
  index,
  visibleTechniciansForDragOrder,
  onMinimizeTechnician,
  isStackHalf = false,
  isStackBottom = false,
  hostTechnicianForUnstack = null,
  ServiceGroup,
  getFilteredTechnicianGroups,
  technicianGroups,
  selectedOrders,
  handleDropBetweenTechnicianSections,
  handleDropBetweenTechnicians,
  handleDropToTechnique,
  handleBoardOrderDragStart,
  handleBoardOrderDragEnd,
  technicianColumnScrollRefs,
  technicianCompletedCounts,
  technicianCompletedDetails,
  isMenuOpen,
  currentUser,
  getTechnicianIdByName,
  avatarUrlByTechId,
  trackableTechnicianIds,
  lunchStartedAtByTechId,
  setTechnicianPanelMock,
  setSelectedFilterItems,
  isRefreshingColumn,
  handleTechnicianColumnUnstack,
  handleTechnicianColumnStack,
  toggleTechnicianMenu,
  updateTechnicianMenuPosition,
  handleColumnReorder,
  boardTechnicianColumnSlots,
  columnOrder,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [showOsV2SummaryTooltip, setShowOsV2SummaryTooltip] = useState(false);
  const [isDragOverColumn, setIsDragOverColumn] = useState(false);
  const [isDragOverStack, setIsDragOverStack] = useState(false);
  const [activeSessionDropZone, setActiveSessionDropZone] = useState(null);
  const [orderSessionDropArmed, setOrderSessionDropArmed] = useState(false);
  const columnRootRef = useRef(null);
  const columnHeaderRef = useRef(null);
  const columnContentRef = useRef(null);
  const isOrderDraggingRef = useRef(false);

  const filteredGroups = useMemo(
    () => getFilteredTechnicianGroups(technician),
    [getFilteredTechnicianGroups, technician]
  );

  const allTechnicianOrdersForCondtTabs = useMemo(
    () => getAllTechnicianOrdersForCondtTabs(technicianGroups, technician),
    [technicianGroups, technician]
  );

  const setColumnOrderDragging = useCallback((on) => {
    isOrderDraggingRef.current = on;
    columnRootRef.current?.classList.toggle('dragging-order', on);
  }, []);

  const clearSessionDropUi = useCallback(() => {
    setActiveSessionDropZone(null);
    setOrderSessionDropArmed(false);
    setColumnOrderDragging(false);
  }, [setColumnOrderDragging]);

  const handleSessionZoneDrop = useCallback(
    (e, groupName) => {
      if (isBoardLiveServiceGroup(groupName)) return;
      clearSessionDropUi();

      const dragType = e.dataTransfer.getData('dragType');
      if (dragType === 'column') return;

      const fromTechnician = e.dataTransfer.getData('fromTechnician');
      const fromTechnicianName = e.dataTransfer.getData('fromTechnicianName');
      const fromGroup = e.dataTransfer.getData('fromGroup');
      const orderIds = getBoardDragOrderIds(e.dataTransfer, selectedOrders);

      if (fromTechnician === 'true' && fromTechnicianName) {
        if (fromTechnicianName === technician && fromGroup === groupName) return;
        if (fromTechnicianName === technician && fromGroup !== groupName) {
          handleDropBetweenTechnicianSections(technician, fromGroup, groupName, orderIds);
        } else if (fromTechnicianName !== technician) {
          handleDropBetweenTechnicians(fromTechnicianName, technician, groupName, orderIds);
        }
      } else {
        handleDropToTechnique(technician, groupName, orderIds);
      }
    },
    [
      technician,
      clearSessionDropUi,
      selectedOrders,
      handleDropBetweenTechnicianSections,
      handleDropBetweenTechnicians,
      handleDropToTechnique,
    ]
  );

  const handleColumnOrderDragStart = useCallback(() => {
    setColumnOrderDragging(true);
    handleBoardOrderDragStart();
    const onEnd = () => {
      clearSessionDropUi();
      document.removeEventListener('dragend', onEnd);
    };
    document.addEventListener('dragend', onEnd);
  }, [handleBoardOrderDragStart, setColumnOrderDragging, clearSessionDropUi]);

  const handleColumnOrderDragEnd = useCallback(() => {
    clearSessionDropUi();
    handleBoardOrderDragEnd();
  }, [handleBoardOrderDragEnd, clearSessionDropUi]);

  useLayoutEffect(() => {
    const el = columnContentRef.current;
    if (!el || !technicianColumnScrollRefs?.current) return;
    technicianColumnScrollRefs.current[technician] = columnContentRef;
    const saved = technicianColumnScrollRefs.current._savedPositions?.[technician];
    if (saved != null) {
      el.scrollTop = saved;
      delete technicianColumnScrollRefs.current._savedPositions[technician];
    }
    return () => {
      delete technicianColumnScrollRefs.current[technician];
    };
  }, [technician, technicianColumnScrollRefs]);

  const openCountForV2 = filteredGroups['Previsto para hoje']?.length || 0;
  const closedCountForV2 = technicianCompletedCounts[technician] || 0;
  const allGroupsForV2 = technicianGroups[technician] || {};
  const hojeGroupForV2 = allGroupsForV2['Previsto para hoje'] || [];
  const osV2OpenStats = {
    roteirizadasHoje: hojeGroupForV2.filter((order) => order.RoteiroHoje === 1).length,
    amanha: (allGroupsForV2['Previstas para amanhã'] || []).length,
    futuras: (allGroupsForV2['FUTURA'] || []).length,
  };
  const osV2CompletedDetails = technicianCompletedDetails[technician] || null;
  const countTooltipOpen = showOsV2SummaryTooltip;
  const technicianId = getTechnicianIdByName(technician);
  const avatarUrl = lookupByTechId(avatarUrlByTechId, technicianId, 'get');
  const trackable =
    Number(currentUser?.go_id_produto) === 4 &&
    lookupByTechId(trackableTechnicianIds, technicianId, 'has');
  const lunchStartedAt = lookupByTechId(lunchStartedAtByTechId, technicianId, 'get');

  return (
    <div
      ref={columnRootRef}
      className={[
        'kanban-column',
        'technician-column',
        countTooltipOpen ? 'technician-column--count-tooltip-open' : '',
        orderSessionDropArmed ? 'session-drop-target' : '',
        isStackHalf ? 'is-stack-half' : '',
        isStackBottom ? 'is-stack-bottom' : '',
        isDragOverStack ? 'is-stack-target' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        ref={columnHeaderRef}
        className={`technician-column-header ${countTooltipOpen ? 'count-tooltip-open' : ''} ${isDragging ? 'dragging' : ''} ${isDragOverColumn ? 'drag-over-column' : ''} ${isMenuOpen ? 'menu-open' : ''}`}
        draggable
        onDragStart={(e) => {
          if (isOrderDraggingRef.current) {
            e.preventDefault();
            return;
          }
          setIsDragging(true);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', `column-${index}`);
          e.dataTransfer.setData('columnIndex', index.toString());
          e.dataTransfer.setData('columnTechnician', technician);
          e.dataTransfer.setData('dragType', 'column');
        }}
        onDragEnd={() => {
          setIsDragging(false);
          setIsDragOverColumn(false);
          setIsDragOverStack(false);
        }}
        onDragOver={(e) => {
          const types = Array.from(e.dataTransfer.types || []);
          const isColumnDrag = types.includes('columnIndex') || types.includes('text/plain');

          if (types.includes('fromTechnician') || types.includes('orderId')) {
            return;
          }

          if (isColumnDrag) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setIsDragOverColumn(true);
          }
        }}
        onDragLeave={(e) => {
          const relatedTarget = e.relatedTarget;
          if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
            setIsDragOverColumn(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOverColumn(false);
          setIsDragOverStack(false);

          const dragType = e.dataTransfer.getData('dragType');
          const draggedColumnIndex = parseInt(e.dataTransfer.getData('columnIndex'), 10);

          if (dragType === 'column' && !isNaN(draggedColumnIndex) && draggedColumnIndex !== index) {
            handleColumnReorder(
              draggedColumnIndex,
              index,
              visibleTechniciansForDragOrder || boardTechnicianColumnSlots || columnOrder
            );
          }
        }}
      >
        <div className="technician-column-name-block">
          <TechnicianColumnTitle
            technician={technician}
            canOpenNovoPainel={canShowNovoPainelMenuItem(currentUser)}
            avatarUrl={avatarUrl}
            trackable={trackable}
            lunchStartedAt={lunchStartedAt}
            onSingleActivate={(techName) => {
              setTechnicianPanelMock({ open: true, technician: techName });
            }}
            onDoubleActivate={(techName) => {
              setTechnicianPanelMock({ open: false, technician: null });
              setSelectedFilterItems((prev) => ({
                ...prev,
                tecnico: (prev.tecnico || []).filter((t) => t !== techName),
              }));
            }}
          />
          <TechnicianColumnOsV2Badges
            openCount={openCountForV2}
            closedCount={closedCountForV2}
            openStats={osV2OpenStats}
            completedDetails={osV2CompletedDetails}
            onTooltipOpenChange={setShowOsV2SummaryTooltip}
          />
          {isRefreshingColumn && (
            <div className="column-loading-indicator">
              <div className="loading-spinner"></div>
            </div>
          )}
        </div>
        <div className="technician-header-actions technician-header-actions--compact">
          {isStackBottom && hostTechnicianForUnstack ? (
            <button
              type="button"
              className="technician-unstack-btn board-clickup-icon-btn-anchor"
              aria-label="Desempilhar coluna"
              title="Desempilhar"
              onClick={(e) => {
                e.stopPropagation();
                handleTechnicianColumnUnstack(hostTechnicianForUnstack);
              }}
            >
              <Rows2 size={14} strokeWidth={2.2} aria-hidden />
              <span className="board-clickup-tooltip board-clickup-tooltip--bottom" role="tooltip">
                Desempilhar
              </span>
            </button>
          ) : null}
          <button
            type="button"
            className="technician-minimize-btn board-clickup-icon-btn-anchor"
            aria-label="Minimizar coluna"
            onClick={(e) => {
              e.stopPropagation();
              onMinimizeTechnician?.(technician);
            }}
          >
            <Minus size={15.4} strokeWidth={2.2} aria-hidden />
            <span className="board-clickup-tooltip board-clickup-tooltip--bottom" role="tooltip">
              Minimizar coluna
            </span>
          </button>
          <TechnicianMenuTrigger
            technician={technician}
            isOpen={Boolean(isMenuOpen)}
            onToggle={toggleTechnicianMenu}
            onPositionUpdate={updateTechnicianMenuPosition}
          />
        </div>
      </div>

      <div
        className={`column-content custom-scroll${isDragOverStack ? ' drag-over-stack' : ''}`}
        ref={columnContentRef}
        onDragEnter={(e) => {
          const types = Array.from(e.dataTransfer.types || []);
          if (types.includes('columnIndex') || types.includes('dragType')) {
            if (types.includes('fromTechnician') || types.includes('orderId')) return;
            e.preventDefault();
            setIsDragOverStack(true);
            return;
          }
          if (!isBoardOrderDragEvent(e)) return;
          e.preventDefault();
          setOrderSessionDropArmed(true);
        }}
        onDragOver={(e) => {
          const types = Array.from(e.dataTransfer.types || []);
          if (
            (types.includes('columnIndex') || types.includes('dragType') || types.includes('text/plain')) &&
            !types.includes('fromTechnician') &&
            !types.includes('orderId')
          ) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            setIsDragOverStack(true);
            return;
          }
          if (!isBoardOrderDragEvent(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setOrderSessionDropArmed((prev) => (prev ? prev : true));
        }}
        onDragLeave={(e) => {
          const floatKey = encodeURIComponent(String(technician || ''));
          const floatSelector = `[data-tech-session-drop-floating="${floatKey}"]`;
          const related = e.relatedTarget;
          if (related && typeof related.closest === 'function') {
            if (related.closest(floatSelector)) return;
          }
          const floatEl = document.querySelector(floatSelector);
          const { clientX: x, clientY: y } = e;
          if (floatEl && orderSessionDropArmed) {
            const fr = floatEl.getBoundingClientRect();
            if (x >= fr.left && x <= fr.right && y >= fr.top && y <= fr.bottom) return;
          }
          const rect = e.currentTarget.getBoundingClientRect();
          if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setIsDragOverStack(false);
            if (isBoardOrderDragEvent(e)) {
              setOrderSessionDropArmed(false);
              setActiveSessionDropZone(null);
            }
          }
        }}
        onDrop={(e) => {
          const dragType = e.dataTransfer.getData('dragType');
          const draggedTech =
            e.dataTransfer.getData('columnTechnician') ||
            (() => {
              const slots = visibleTechniciansForDragOrder || boardTechnicianColumnSlots || columnOrder;
              const idx = parseInt(e.dataTransfer.getData('columnIndex'), 10);
              return Number.isFinite(idx) ? slots[idx] : '';
            })();
          if (dragType === 'column' && draggedTech && draggedTech !== technician) {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOverStack(false);
            const host =
              isStackBottom && hostTechnicianForUnstack
                ? hostTechnicianForUnstack
                : technician;
            handleTechnicianColumnStack(host, draggedTech);
          }
        }}
      >
        {isDragOverStack ? (
          <div className="technician-stack-drop-hint" aria-live="polite">
            <Rows2 size={14} strokeWidth={2.2} aria-hidden className="technician-stack-drop-hint-icon" />
            <span className="technician-stack-drop-hint-text">
              Empilhar junto com{' '}
              <span className="technician-stack-drop-hint-sep" aria-hidden>
                ›
              </span>{' '}
              <strong className="technician-stack-drop-hint-name">{technician}</strong>
            </span>
          </div>
        ) : null}
        <TechnicianColumnSessionDropOverlay
          armed={orderSessionDropArmed}
          activeZone={activeSessionDropZone}
          onZoneDragEnter={setActiveSessionDropZone}
          onZoneDragLeave={() => setActiveSessionDropZone(null)}
          onZoneDrop={handleSessionZoneDrop}
          anchorRef={columnContentRef}
          anchorId={technician}
          preferFloat={isStackHalf}
          onKeepArmed={() => {
            setOrderSessionDropArmed(true);
          }}
          onDisarm={() => {
            setOrderSessionDropArmed(false);
            setActiveSessionDropZone(null);
          }}
        />
        {Object.entries(filteredGroups).map(([groupName, groupOrders]) => (
          <ServiceGroup
            key={groupName}
            groupName={groupName}
            orders={groupOrders}
            technician={technician}
            allTechnicianOrdersForCondtTabs={allTechnicianOrdersForCondtTabs}
            isDroppable={!isBoardLiveServiceGroup(groupName)}
            onBoardOrderDragStart={handleColumnOrderDragStart}
            onBoardOrderDragEnd={handleColumnOrderDragEnd}
          />
        ))}
      </div>
    </div>
  );
}

export default memo(TechnicianColumn);
export { TechnicianColumn };
